import { buildAdminThemeOptions } from "@/style/adminThemeConfig";

export const SVG_NAMESPACE = "http://www.w3.org/2000/svg";

const requireThemeString = (value: unknown, token: string): string => {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`Missing Admin theme token for ${token}.`);
  }
  return value;
};

const requirePaletteValue = (
  colour: unknown,
  property: "contrastText" | "main",
  token: string,
): string => {
  if (typeof colour !== "object" || colour === null) {
    throw new Error(`Missing Admin theme token for ${token}.`);
  }

  return requireThemeString(
    (colour as Record<string, unknown>)[property],
    token,
  );
};

const lightTheme = buildAdminThemeOptions("light");
const PRESENTATION = {
  accent: requirePaletteValue(
    lightTheme.palette?.primary,
    "main",
    "primary.main",
  ),
  background: requireThemeString(
    lightTheme.palette?.background?.default,
    "background.default",
  ),
  border: requireThemeString(lightTheme.palette?.divider, "divider"),
  card: requireThemeString(
    lightTheme.palette?.background?.paper,
    "background.paper",
  ),
  fontFamily: "var(--hellocashflow-font-family, Poppins, sans-serif)",
  muted: requireThemeString(
    lightTheme.palette?.text?.secondary,
    "text.secondary",
  ),
  text: requireThemeString(lightTheme.palette?.text?.primary, "text.primary"),
} as const;

// Keep Mermaid cluster headings inside the sanitized view box without generated styles.
const DIAGRAM_PADDING = 24;

const setAttributes = (
  elements: Iterable<Element>,
  attributes: Readonly<Record<string, string>>,
): void => {
  for (const element of elements) {
    for (const [name, value] of Object.entries(attributes)) {
      element.setAttribute(name, value);
    }
  }
};

const removeAttributes = (
  elements: Iterable<Element>,
  attributes: readonly string[],
): void => {
  for (const element of elements) {
    for (const attribute of attributes) {
      element.removeAttribute(attribute);
    }
  }
};

const expandViewBox = (
  svg: SVGSVGElement,
): { height: number; width: number; x: number; y: number } | null => {
  const values = (svg.getAttribute("viewBox") ?? "")
    .trim()
    .split(/[\s,]+/u)
    .map(Number);
  if (
    values.length !== 4 ||
    values.some((value) => !Number.isFinite(value)) ||
    (values[2] ?? 0) <= 0 ||
    (values[3] ?? 0) <= 0
  ) {
    return null;
  }

  const [originalX = 0, originalY = 0, originalWidth = 0, originalHeight = 0] =
    values;
  const padded = {
    height: originalHeight + DIAGRAM_PADDING * 2,
    width: originalWidth + DIAGRAM_PADDING * 2,
    x: originalX - DIAGRAM_PADDING,
    y: originalY - DIAGRAM_PADDING,
  };
  svg.setAttribute(
    "viewBox",
    `${padded.x} ${padded.y} ${padded.width} ${padded.height}`,
  );
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  return padded;
};

const positionClusterLabels = (svg: SVGSVGElement): void => {
  for (const cluster of svg.querySelectorAll("g.cluster")) {
    const boundary = Array.from(cluster.children).find(
      (child) => child.localName === "rect",
    );
    const label = Array.from(cluster.children).find((child) =>
      child.classList.contains("cluster-label"),
    );
    const boundaryX = Number(boundary?.getAttribute("x"));
    const labelTransform = label?.getAttribute("transform") ?? "";
    const translate =
      /^translate\(\s*[-+]?\d*\.?\d+(?:e[-+]?\d+)?[\s,]+([-+]?\d*\.?\d+(?:e[-+]?\d+)?)\s*\)$/iu.exec(
        labelTransform,
      );
    const labelY = Number(translate?.[1]);

    if (label && Number.isFinite(boundaryX) && Number.isFinite(labelY)) {
      label.setAttribute("transform", `translate(${boundaryX + 16} ${labelY})`);
    }
  }
};

const centreEdgeLabels = (svg: SVGSVGElement): void => {
  for (const edgeLabel of svg.querySelectorAll("g.edgeLabel")) {
    const background = edgeLabel.querySelector("rect.background");
    const text = edgeLabel.querySelector("text");
    const outerTspan = text
      ? Array.from(text.children).find((child) => child.localName === "tspan")
      : null;
    const x = Number(background?.getAttribute("x"));
    const y = Number(background?.getAttribute("y"));
    const width = Number(background?.getAttribute("width"));
    const height = Number(background?.getAttribute("height"));

    if (
      !text ||
      !outerTspan ||
      ![x, y, width, height].every(Number.isFinite) ||
      width <= 0 ||
      height <= 0
    ) {
      continue;
    }

    // Mermaid translates the label container by half its measured size. Its
    // generated CSS normally positions the text inside that local box, but the
    // dedicated sanitizer deliberately removes generated styles. Restore the
    // box centre explicitly so relationship text sits on the connector point
    // instead of drifting half a label-width to the left or above it.
    const centreX = x + width / 2;
    const centreY = y + height / 2;
    setAttributes([text, outerTspan], {
      x: String(centreX),
      y: String(centreY),
    });
  }
};

export const applySafeArchitecturePresentation = (svg: SVGSVGElement): void => {
  const viewBox = expandViewBox(svg);
  positionClusterLabels(svg);
  const background = svg.ownerDocument.createElementNS(SVG_NAMESPACE, "rect");
  setAttributes([background], {
    class: "architecture-diagram-background",
    fill: PRESENTATION.card,
    height: viewBox ? String(viewBox.height) : "100%",
    stroke: "none",
    width: viewBox ? String(viewBox.width) : "100%",
    x: viewBox ? String(viewBox.x) : "0",
    y: viewBox ? String(viewBox.y) : "0",
  });
  svg.insertBefore(background, svg.firstChild);

  setAttributes(svg.querySelectorAll("text, tspan"), {
    fill: PRESENTATION.text,
    "font-family": PRESENTATION.fontFamily,
    "font-size": "15px",
    "font-weight": "600",
    "text-anchor": "middle",
  });
  // Mermaid positions SVG text for its generated style metrics. The sanitizer
  // removes those styles, so restore the equivalent baseline offset explicitly
  // to keep one- and multi-line labels centred inside their nodes.
  setAttributes(svg.querySelectorAll("g.node text"), {
    transform: "translate(0 17)",
  });
  setAttributes(
    svg.querySelectorAll(
      "g.node > rect, g.node > circle, g.node > ellipse, g.node > polygon, g.node > path",
    ),
    {
      fill: PRESENTATION.card,
      rx: "8",
      ry: "8",
      stroke: PRESENTATION.border,
      "stroke-width": "1.25",
    },
  );
  const clusterBoundaries = svg.querySelectorAll("g.cluster > rect");
  setAttributes(clusterBoundaries, {
    fill: PRESENTATION.background,
    opacity: "1",
    rx: "10",
    ry: "10",
    stroke: PRESENTATION.border,
    "stroke-width": "1",
  });
  removeAttributes(clusterBoundaries, ["stroke-dasharray"]);
  setAttributes(svg.querySelectorAll("g.cluster-label rect.background"), {
    fill: PRESENTATION.card,
    opacity: "1",
    stroke: "none",
  });
  setAttributes(svg.querySelectorAll("g.cluster text, g.cluster tspan"), {
    fill: PRESENTATION.text,
    "font-size": "13px",
    "font-weight": "700",
    "letter-spacing": "0.03em",
  });
  setAttributes(
    svg.querySelectorAll("g.cluster-label text, g.cluster-label tspan"),
    {
      "text-anchor": "start",
    },
  );
  setAttributes(svg.querySelectorAll("g.cluster-label text"), {
    transform: "translate(0 22)",
  });
  setAttributes(svg.querySelectorAll("g.edgePath path, path.flowchart-link"), {
    fill: "none",
    stroke: PRESENTATION.muted,
    "stroke-width": "1.5",
  });
  setAttributes(svg.querySelectorAll("marker path, marker polygon"), {
    fill: PRESENTATION.muted,
    stroke: PRESENTATION.muted,
  });
  setAttributes(svg.querySelectorAll("g.edgeLabel text, g.edgeLabel tspan"), {
    "dominant-baseline": "central",
    fill: PRESENTATION.muted,
    "font-size": "13px",
    "font-weight": "500",
    "paint-order": "stroke fill",
    stroke: PRESENTATION.card,
    "stroke-linejoin": "round",
    "stroke-width": "7",
  });
  setAttributes(svg.querySelectorAll("g.edgeLabel rect"), {
    fill: PRESENTATION.card,
    opacity: "1",
    rx: "4",
    ry: "4",
    stroke: "none",
  });
  centreEdgeLabels(svg);

  setAttributes(
    svg.querySelectorAll(
      "g.node.focal > rect, g.node.focal > circle, g.node.focal > ellipse, g.node.focal > polygon, g.node.focal > path",
    ),
    {
      fill: PRESENTATION.accent,
      "fill-opacity": "0.08",
      stroke: PRESENTATION.accent,
      "stroke-width": "2.25",
    },
  );
  setAttributes(svg.querySelectorAll("g.node.focal text, g.node.focal tspan"), {
    fill: PRESENTATION.text,
    "font-weight": "700",
  });
  setAttributes(
    svg.querySelectorAll(
      "g.node.inactive > rect, g.node.inactive > circle, g.node.inactive > ellipse, g.node.inactive > polygon, g.node.inactive > path",
    ),
    {
      fill: PRESENTATION.background,
      "fill-opacity": "1",
      stroke: PRESENTATION.muted,
      "stroke-dasharray": "6 5",
      "stroke-width": "1.25",
    },
  );
  setAttributes(
    svg.querySelectorAll("g.node.inactive text, g.node.inactive tspan"),
    {
      fill: PRESENTATION.muted,
    },
  );
};
